import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, CheckCircle2, XCircle, Bot, ShieldCheck, Link2, AlertTriangle } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { api, formatApiError, fmtDate } from "../lib/api";

const CopyField = ({ label, value, testid }) => (
  <div className="space-y-1">
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className="flex items-stretch gap-2">
      <code className="flex-1 min-w-0 truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-800" data-testid={`${testid}-value`}>
        {value || "—"}
      </code>
      <Button
        variant="outline" size="sm" className="shrink-0"
        onClick={() => { navigator.clipboard.writeText(value || ""); toast.success("Disalin ke clipboard"); }}
        data-testid={`${testid}-copy`}
      >
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

export default function ChatGptConnector() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mcp-admin/info");
      setInfo(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat info konektor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const connected = info?.connected;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="chatgpt-connector-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-cocoa-dark flex items-center justify-center text-ivory shrink-0">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Konektor ChatGPT</h1>
              <p className="text-sm text-slate-500">Hubungkan ChatGPT ke ERP (read-only) via MCP. Hanya Owner.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="connector-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Muat ulang
          </Button>
        </div>

        {/* Status */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex flex-wrap items-center justify-between gap-4" data-testid="connector-status-card">
          <div className="flex items-center gap-3">
            {connected
              ? <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              : <XCircle className="w-8 h-8 text-slate-300" />}
            <div>
              <div className="text-sm font-semibold text-slate-900" data-testid="connector-status-text">
                {connected ? "Terhubung" : "Belum terhubung"}
              </div>
              <div className="text-xs text-slate-500">
                {info?.active_sessions || 0} sesi aktif · {info?.registered_clients || 0} client terdaftar
                {info?.last_authorized_at ? ` · terakhir ${fmtDate(info.last_authorized_at)}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Read-only · scope {info?.scope || "erp:read"}
          </div>
        </div>

        {info?.is_preview && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 text-sm text-amber-800" data-testid="connector-preview-warning">
            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-500" />
            <div>Ini URL <b>preview</b>. ChatGPT butuh URL produksi HTTPS yang stabil — <b>deploy</b> aplikasi lebih dulu, lalu gunakan URL produksi di sini.</div>
          </div>
        )}

        {/* URL utama */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="connector-url-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Link2 className="w-4 h-4 text-cocoa" /> Alamat Konektor (MCP)
          </div>
          <CopyField label="URL MCP — tempel ini di ChatGPT" value={info?.mcp_url} testid="mcp-url" />
          <div className="grid sm:grid-cols-2 gap-4">
            <CopyField label="Issuer OAuth" value={info?.issuer} testid="issuer" />
            <CopyField label="Protected-resource metadata" value={info?.protected_resource_metadata} testid="prm" />
          </div>
        </div>

        {/* Cara menghubungkan */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3" data-testid="connector-howto-card">
          <div className="text-sm font-semibold text-slate-900">Cara menghubungkan</div>
          <ol className="list-decimal ml-5 space-y-1.5 text-sm text-slate-600">
            <li>Buka ChatGPT → <b>Settings → Connectors</b> → <b>Add custom connector</b>.</li>
            <li>Tempel <b>URL MCP</b> di atas.</li>
            <li>ChatGPT otomatis mendeteksi OAuth & membuka halaman login ERP. Masuk dengan akun <b>Owner</b> lalu <b>Izinkan Akses</b>.</li>
            <li>Setelah tertaut, ChatGPT bisa memanggil {info?.tool_count || 0} tool read-only di bawah.</li>
          </ol>
          <p className="text-xs text-slate-400">Jika daftar tool berubah, di ChatGPT lakukan Disconnect lalu Reconnect agar schema terbaru terbaca.</p>
        </div>

        {/* Daftar tool */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="connector-tools-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">Tool tersedia (read-only)</div>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2.5 py-1" data-testid="connector-tool-count">
              {info?.tool_count || 0} tool
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {(info?.tools || []).map((t) => (
              <div key={t.name} className="py-2.5 flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3" data-testid={`tool-${t.name}`}>
                <div className="sm:w-56 shrink-0">
                  <div className="text-[13px] font-semibold text-slate-800">{t.title}</div>
                  <code className="text-[11px] text-cocoa">{t.name}</code>
                </div>
                <div className="text-[13px] text-slate-500 leading-snug">{t.description}</div>
              </div>
            ))}
            {!loading && (info?.tools || []).length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400">Tidak ada tool.</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
